// frontend/src/components/projects/ModulePermissionsMatrix.tsx
import React, { useMemo } from "react";
import { Checkbox, Table, Text } from "@mantine/core";
import type { IModuleAccessLevel, IMultiplacerModuleKey } from "@multiconsult-group/multiplacer-core";
import { useAppSelector } from "../app/hooks";
import { selectVisibleModulesForCurrentProject } from "../projectSelectors";

export type UIModuleAccess = "NONE" | IModuleAccessLevel; // "NONE" | "READ" | "WRITE"

type Props = {
  accessByModule: Record<IMultiplacerModuleKey, UIModuleAccess>;
  onChange: (next: Record<IMultiplacerModuleKey, UIModuleAccess>) => void;
};

export const ModulePermissionsMatrix: React.FC<Props> = ({ accessByModule, onChange }) => {
  const visibleModules = useAppSelector(selectVisibleModulesForCurrentProject);
  const rows = useMemo(
    () =>
      visibleModules.map((m) => ({
        key: m.key as IMultiplacerModuleKey,
        title: m.title,
      })),
    [visibleModules]
  );

  const setAccess = (module: IMultiplacerModuleKey, next: UIModuleAccess) => {
    onChange({ ...accessByModule, [module]: next });
  };

  return (
    <>
      <Text fw={600}>Moduler</Text>

      <Table withColumnBorders withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Modul</Table.Th>
            <Table.Th style={{ width: 120 }}>Les</Table.Th>
            <Table.Th style={{ width: 120 }}>Skriv</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {rows.map((r) => {
            const access: UIModuleAccess = accessByModule[r.key] ?? "NONE";
            const isRead = access === "READ" || access === "WRITE";
            const isWrite = access === "WRITE";

            return (
              <Table.Tr key={r.key}>
                <Table.Td>{r.title}</Table.Td>

                <Table.Td>
                  <Checkbox
                    checked={isRead}
                    onChange={(e) => {
                      const checked = e.currentTarget.checked;

                      if (checked) {
                        // Turn READ on -> at least READ (keep WRITE if already set)
                        setAccess(r.key, access === "WRITE" ? "WRITE" : "READ");
                      } else {
                        // Turn READ off -> also disables WRITE
                        setAccess(r.key, "NONE");
                      }
                    }}
                  />
                </Table.Td>

                <Table.Td>
                  <Checkbox
                    checked={isWrite}
                    onChange={(e) => {
                      const checked = e.currentTarget.checked;

                      if (checked) {
                        // WRITE implies READ
                        setAccess(r.key, "WRITE");
                      } else {
                        // Turning WRITE off leaves READ on
                        setAccess(r.key, "READ");
                      }
                    }}
                    // If not read, you can't toggle WRITE on (since it implies READ)
                    disabled={!isRead}
                  />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </>
  );
};
